import React, { useEffect, useRef, useMemo } from "react";
import { getErrorInfo } from "../data/errorCodes";
import "./SpaceErrorPage.css";

// ── Planetas mini que orbitan según el código de error ──────────────
const PLANET_COLORS = [
  { center: "#1FC8CC", mid: "#0e8d92", dark: "#065458" },
  { center: "#2DD4BF", mid: "#0F766E", dark: "#042F2E" },
  { center: "#67E8F9", mid: "#0E7490", dark: "#082F49" },
  { center: "#34D399", mid: "#059669", dark: "#022C22" },
];

function Planet({ size = 96, colorIdx = 0 }) {
  const c = PLANET_COLORS[colorIdx % PLANET_COLORS.length];
  const id = `pg-${colorIdx}`;
  const atmId = `atm-${colorIdx}`;
  const glowId = `glow-${colorIdx}`;
  const r = size / 2;
  const pr = Math.round(size * 0.31);

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      fill="none"
      aria-hidden="true"
    >
      <defs>
        <radialGradient id={id} cx="32%" cy="28%" r="68%">
          <stop offset="0%" stopColor={c.center} />
          <stop offset="45%" stopColor={c.mid} />
          <stop offset="80%" stopColor={c.dark} />
          <stop offset="100%" stopColor="#020E0F" />
        </radialGradient>
        <radialGradient id={atmId} cx="50%" cy="50%" r="50%">
          <stop offset="65%" stopColor="transparent" />
          <stop offset="100%" stopColor="rgba(14,141,146,0.35)" />
        </radialGradient>
        <radialGradient id={glowId} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgba(14,141,146,0.18)" />
          <stop offset="100%" stopColor="transparent" />
        </radialGradient>
      </defs>
      <circle cx={r} cy={r} r={r - 2} fill={`url(#${glowId})`} />
      <circle cx={r} cy={r} r={pr} fill={`url(#${id})`} />
      <circle cx={r} cy={r} r={pr} fill={`url(#${atmId})`} />
      <ellipse
        cx={r - 3}
        cy={r - 11}
        rx={pr * 0.72}
        ry={pr * 0.12}
        fill="rgba(31,200,204,0.13)"
        transform={`rotate(-18 ${r - 3} ${r - 11})`}
      />
      <ellipse
        cx={r + 2}
        cy={r + 6}
        rx={pr * 0.52}
        ry={pr * 0.08}
        fill="rgba(6,84,88,0.42)"
        transform={`rotate(-18 ${r + 2} ${r + 6})`}
      />
      <circle cx={r - 9} cy={r - 7} r={pr * 0.17} fill="rgba(6,60,64,0.55)" />
      <circle
        cx={r + 6}
        cy={r - 15}
        r={pr * 0.08}
        fill="rgba(31,200,204,0.25)"
      />
    </svg>
  );
}

// ── Nebulosa en canvas ───────────────────────────────────────────────
function NebulaCanvas() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const parent = canvas.parentElement;
    canvas.width = parent.offsetWidth || 800;
    canvas.height = parent.offsetHeight || 600;
    const ctx = canvas.getContext("2d");

    const spots = [
      { x: 0.22, y: 0.3, r: 0.55, color: "rgba(14,141,146,0.2)" },
      { x: 0.8, y: 0.65, r: 0.42, color: "rgba(6,84,88,0.16)" },
      { x: 0.5, y: 0.5, r: 0.3, color: "rgba(2,30,32,0.28)" },
      { x: 0.1, y: 0.75, r: 0.28, color: "rgba(14,141,146,0.1)" },
    ];

    spots.forEach(({ x, y, r, color }) => {
      const grd = ctx.createRadialGradient(
        canvas.width * x,
        canvas.height * y,
        0,
        canvas.width * x,
        canvas.height * y,
        canvas.width * r,
      );
      grd.addColorStop(0, color);
      grd.addColorStop(1, "transparent");
      ctx.fillStyle = grd;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    });
  }, []);

  return <canvas ref={ref} className="sep-nebula" aria-hidden="true" />;
}

// ── Estrellas ────────────────────────────────────────────────────────
function Stars({ count = 70 }) {
  const stars = useMemo(() => {
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      top: `${Math.random() * 100}%`,
      left: `${Math.random() * 100}%`,
      size: Math.random() * 1.8 + 0.3,
      opacity: 0.15 + Math.random() * 0.7,
      isTeal: Math.random() > 0.88,
      delay: `${Math.random() * 4}s`,
      dur: `${2.5 + Math.random() * 3}s`,
    }));
  }, [count]);

  return (
    <div className="sep-stars" aria-hidden="true">
      {stars.map((s) => (
        <span
          key={s.id}
          className="sep-star"
          style={{
            top: s.top,
            left: s.left,
            width: s.size,
            height: s.size,
            opacity: s.opacity,
            background: s.isTeal ? "rgba(14,141,146,0.9)" : "white",
            animationDelay: s.delay,
            animationDuration: s.dur,
          }}
        />
      ))}
    </div>
  );
}

// ── Shooting stars ───────────────────────────────────────────────────
const SHOOTERS = [
  { top: "12%", left: "5%", width: 130, delay: "0s" },
  { top: "28%", left: "55%", width: 90, delay: "2.8s" },
  { top: "68%", left: "18%", width: 70, delay: "4.3s" },
  { top: "45%", left: "75%", width: 110, delay: "6.1s" },
];

// ── Componente principal ─────────────────────────────────────────────
export default function SpaceErrorPage({ status = "404" }) {
  const error = getErrorInfo(status);

  // Índice de color del planeta varía por familia de error
  const colorIdx = error.code >= 500 ? 2 : (error.code - 400) % 4;

  // Anillo del planeta
  const ringStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    width: 140,
    height: 44,
    border: "2px solid rgba(14,141,146,0.5)",
    borderRadius: "50%",
    transform: "translate(-50%, -50%) rotateX(70deg)",
    boxShadow: "0 0 12px rgba(14,141,146,0.2)",
    pointerEvents: "none",
  };

  return (
    <div className="sep-page" role="main">
      <NebulaCanvas />
      <Stars />

      {SHOOTERS.map((s, i) => (
        <span
          key={i}
          className="sep-shooting"
          aria-hidden="true"
          style={{
            top: s.top,
            left: s.left,
            width: s.width,
            animationDelay: s.delay,
          }}
        />
      ))}

      <div className="sep-content">
        <img
          src="/images/logom.svg"
          alt="Heuristiko Logo"
          style={{ height: 60, paddingBottom: 12 }}
        />
        {/* Badge de estado */}
        <div className="sep-badge" aria-label="Estado del sistema">
          <span className="sep-badge-dot" />
          <span>SISTEMA ACTIVO</span>
        </div>
        {/* Planeta animado */}
        <div className="sep-planet-wrap" aria-hidden="true">
          <Planet size={96} colorIdx={colorIdx} />
          <div style={ringStyle} />
        </div>

        {/* Código de error */}
        <h1 className="sep-code" aria-label={`Error ${error.code}`}>
          {error.code}
        </h1>

        {/* Divisor */}
        <div className="sep-divider" aria-hidden="true">
          <span className="sep-line" />
          <span className="sep-diamond" />
          <span className="sep-line" />
        </div>

        {/* Hint técnico + título */}
        <p className="sep-hint">{error.hint}</p>
        <h2 className="sep-title">{error.title}</h2>

        {/* Mensaje descriptivo */}
        <p className="sep-message">{error.message}</p>

        {/* Acción */}
        <a href="https://heuristiko.com" className="sep-btn">
          ← Volver al inicio
        </a>
      </div>
    </div>
  );
}
